<template>
    <div class="article-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && article.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="article"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <template v-if="editMode">
                                <button
                                    v-if="article.isPinned"
                                    class="btn btn-info btn-static waves-effect waves-light m-1"
                                    @click="unpin"
                                >
                                    <i class="fas fa-eraser" />
                                    Unpin
                                </button>
                                <button
                                    v-else
                                    class="btn btn-info btn-static waves-effect waves-light m-1"
                                    @click="pin"
                                >
                                    <i class="fas fa-map-pin" />
                                    Pin
                                </button>
                                <a
                                    :href="previewUrl"
                                    class="btn btn-primary btn-static waves-effect waves-light m-1"
                                    target="_blank"
                                    @click="onPreviewButtonClick"
                                >
                                    <i class="far fa-eye" />
                                    Preview
                                </a>
                            </template>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="article"
                            :slug="article.slug"
                            :options="{ slugBasicUrl: 'articles' }"
                            :slug-error="slugError"
                            :featured-image="article.featuredImageId"
                            get-categories-function-name="articleCategories/index"
                            :category="article.category"
                            :campuses="article.campuses"
                            get-tags-function-name="articleTags/index"
                            :tags="article.tags"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-category-change="onCategoryChange"
                            @on-campuses-change="onCampusesChange"
                            @on-tags-change="onTagsChange"
                            @on-featured-image-change="onFeaturedImageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import AddEditForm from '@/components/articles/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Article'
    },

    validations: {
        article: {
            name: {
                required
            },
            slug: {
                required(value) {
                    const isRequired = this.editMode;

                    return !!(!isRequired || (isRequired && value));
                }
            },
            content: {
                atLeastOneItem: val => val && val.length > 0
            },
            status: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit article' : 'Add article',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Articles',
                    to: '/articles'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            article: {
                content: [],
                meta: {},
                status: 'published',
                isPremium: false,
                isFeatured: false,
                tags: [],
                campuses: []
            },
            informationKey: 0,
            serverErrors: [],
            articleValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true,
                category: true,
                featuredImage: true,
                campuses: true,
                tags: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/articles/${this.article.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.article,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.article = await this.getArticle(this.id);
                this.articleValueStringify = JSON.stringify(this.article);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.article.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            addArticle: 'articles/store',
            deleteArticle: 'articles/destroy',
            getArticle: 'articles/show',
            updateArticle: 'articles/update',
            pinArticle: 'articles/pin',
            unpinArticle: 'articles/unpin',
            getCampuses: 'campuses/index'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const tags = this.article.tags.map(tag => tag.id);
            const campuses = this.article.campuses.map(campus => campus.id);

            const data = {
                ...this.article,
                tags,
                campuses,
                isFeatured: !!this.article.isFeatured
            };

            try {
                if (!this.editMode) {
                    const added = await this.addArticle(data);

                    this.$router.push(`/articles/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateArticle({
                        id: this.id,
                        data
                    });

                    this.article.updatedAt = updatedAt;
                    this.articleValueStringify = JSON.stringify(this.article);

                    this.$toaster('Article updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteArticle(this.id);

                this.$router.push('/articles');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        async pin() {
            try {
                await this.pinArticle(this.id);

                this.article.isPinned = true;

                this.$toaster('Article has been pinned.');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        async unpin() {
            try {
                await this.unpinArticle(this.id);

                this.article.isPinned = false;

                this.$toaster('Article has been unpinned.');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.articleValueStringify !== JSON.stringify(this.article)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.article.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.article.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.article.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.article.featuredImageId = imageId;
        },

        onCategoryChange(category) {
            this.article.category = category;
            this.article.categoryId = category ? category.id : null;
            this.informationKey++;
        },

        onCampusesChange(campuses) {
            this.article.campuses = campuses;
            this.informationKey++;
        },

        onTagsChange(tags) {
            this.article.tags = tags;
            this.informationKey++;
        }
    }
};
</script>
